import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import { createElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import usePrintHelper from 'pages/Settings/Print/hooks/useHelpers';

import AwbTemplate1 from './AWBTemplates/AwbTemplate1';
import Template1 from './Template1';
import Template2 from './Template2';
import Template3 from './Template3';
import Template4 from './Template4';
import Template5 from './Template5';
import defaultStyles from './defaultStyles';
import usePrintLocale from './usePrintLocale';

export const pdfScales = {
	A4: 1,
	A5: 0.8,
	A6: 0.6,
	A7: 0.4,
	'100x100': 0.4,
	'100x120': 0.4
};

export const pageSizes = {
	A4: 'A4',
	A5: 'A5',
	A6: 'A6',
	A7: 'A7',
	'100x100': { width: 283, height: 283, key: '100x100' }, // 100x100mm is 283x283px in 72dpi
	'100x120': { width: 283, height: 340, key: '100x120' } // 100x120mm is 283x340px in 72dpi
};

const MainTemplate = (props) => {
	const { printLocale } = usePrintLocale();
	const { t } = useTranslation('translation', {
		lng: printLocale
	});
	const { getTemplateFontWeight, getTemplateFontSize } = usePrintHelper();

	const { propJSON, fieldName } = props;
	const pageSize = useMemo(() => pageSizes[propJSON?.pageSize] ?? 'A4', [propJSON?.pageSize]);
	const pageScale = useMemo(() => (pageSize?.key ? pdfScales[pageSize?.key] : pdfScales[pageSize]), [pageSize]);
	const fontSize = useMemo(() => getTemplateFontSize(propJSON?.fontSize, pageScale), [propJSON?.fontSize, pageScale]);
	const fontWeight = useMemo(() => getTemplateFontWeight(propJSON?.fontWeight ?? 'regular'), [propJSON?.fontWeight]);

	const styles = useMemo(
		() =>
			StyleSheet.create({
				page: {
					paddingHorizontal: propJSON?.pageMarginHorizontal ?? 25 * pageScale,
					...(fieldName === 'awbPrintPropJSON' && propJSON?.showFooterOnTop
						? {
								paddingTop: (propJSON?.pageMarginVertical ?? 20) + fontSize * 2.5,
								paddingBottom: propJSON?.pageMarginVertical ?? 10
							}
						: {
								paddingTop: propJSON?.pageMarginVertical ?? 20,
								paddingBottom: (propJSON?.pageMarginVertical ?? 10) + fontSize * 2.5
							}),
					fontSize: `${fontSize}pt`,
					fontFamily: defaultStyles.text.fontFamily,
					color: 'black',
					fontWeight,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					width: '100%'
				},
				footer: {
					position: 'absolute',
					width: '100%',
					...(fieldName === 'awbPrintPropJSON' && propJSON?.showFooterOnTop
						? { top: 0, paddingTop: propJSON?.pageMarginVertical ?? 20 }
						: { bottom: 0, paddingBottom: propJSON?.pageMarginVertical ?? 10 })
				},
				footerContent: {
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'center',
					alignItems: 'center',
					marginTop: 'auto',
					width: '100%',
					...(fieldName === 'awbPrintPropJSON' && propJSON?.hidePoweredByOto ? { opacity: '0' } : {})
				},
				logo: {
					width: fontSize
				},
				pageNumber: {
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-end',
					...(fieldName === 'awbPrintPropJSON' && propJSON?.hidePageNumber ? { opacity: '0' } : {})
				},
				tableRow: {
					height: 'auto',
					flexDirection: 'row',
					alignItems: 'center'
				}
			}),
		[fontSize, pageScale, fontWeight]
	);

	const templates = useMemo(
		() => ({
			invoicePrintPropJSON: {
				template1: Template1, // Order Print with QR Code
				template2: Template2, // Order Print - QR Code (with Arabic) (A4) Template - 2
				template3: Template3, // Order Print with QR Code (A4) Template - 2
				template4: Template4, // 4x6 Order Label (without Items)
				template5: Template5 // Government Invoice Template (A4)
			},
			awbPrintPropJSON: {
				template1: AwbTemplate1 // Airway Bill Template
			},
			branchPrintPropJSON: {
				template1: Template1, // Label Template 1
				template2: Template2, // Label Template 2
				template3: Template3, // Label Template 3
				template4: Template4, // Label Template 4
				template5: Template5 // Label Template 5
			}
		}),
		[]
	);

	return (
		<Document pageMode="fullScreen">
			<Page size={pageSize} style={styles.page} wrap>
				{templates[fieldName][propJSON.templateName]
					? createElement(templates[fieldName][propJSON.templateName], props)
					: createElement(<Text>{t('notAvailable')}</Text>)}
				<View style={[styles.footer]} fixed>
					<View>
						<View style={[styles.footerContent]}>
							<Text> {t('poweredBy')} </Text>
							<Image
								src="https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png"
								style={styles.logo}
							/>
						</View>
					</View>
					<View style={styles.pageNumber}>
						<Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
					</View>
				</View>
			</Page>
		</Document>
	);
};

export default MainTemplate;
